import { setRuntimeConfig } from 'lib/runtimeConfig';
import { configureStore } from 'domain/store';
import tokenKeeper from 'lib/apiTokenKeeper';
import router from 'domain/router/router';
import rootSaga from 'sagas/index';
import { Auth } from 'aws-amplify';
import { CognitoAuthError, ConfigFetchError, ConfigParseError } from 'lib/customErrors';
import * as Sentry from '@sentry/browser';

const DEFAULT_ENV = process.env.REACT_APP_ENV === 'local' ? process.env : {};

const ENV_PREFIX = 'REACT_APP_';

async function envConfigInitializer() {
  let jwtToken = null;
  let response = null;
  try {
    const session = await Auth.currentSession();
    jwtToken = session.getAccessToken().getJwtToken();
  } catch (e) {
    // this must be run to allow saga to logout user respecting backurl and other existing logout logic
    const store = configureStore(router.navigate);
    tokenKeeper.injectStore(store);
    store.runSaga(rootSaga, store.dispatch);

    throw new CognitoAuthError('User is not authorized or token retieval failed');
  }

  response = await fetch(`${process.env.REACT_APP_ENV_CONFIG_API_URL}/v2/frontend/get_configuration`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${jwtToken}` },
  });

  if (!response.ok) {
    console.error('Failed to get envs');
    Sentry.captureMessage('Server config fetch failed', {
      level: 'warning',
      tags: { source: 'SERVER_CONFIG_FETCH_FAILED' },
      extra: {
        cause: response, // @to-do stringify it somehow
      },
    });
    // throwing error to catch it in the main flow and render SWW page in case error is instance of ConfigFetchError
    throw new ConfigFetchError('Failed to fetch env config file');
  }

  try {
    const { whitelabel = {}, configuration = {} } = await response.json();
    const env = Object.entries(configuration).reduce((acc, [k, v]) => {
      const key = `${ENV_PREFIX}${k}`;

      acc[key] = DEFAULT_ENV[k] || v;

      return acc;
    }, {});

    const wl = Object.entries(whitelabel).reduce((acc, [k, v]) => {
      acc[k] = v === '0' || v === '1' ? Boolean(Number(v)) : v;

      return acc;
    }, {});

    setRuntimeConfig('env', env);
    setRuntimeConfig('whitelabel', wl);
  } catch (e) {
    console.error('Failed to parse fetched env config');
    Sentry.captureMessage('Server config parse failed', {
      level: 'warning',
      tags: { source: 'SERVER_CONFIG_PARSE_FAILED' },
      extra: {
        cause: e,
      },
    });
    // throwing error to catch it in the main flow and render SWW page in in case error is instance of ConfigFetchError
    throw new ConfigParseError('Failed to parse env config file');
  }
}

export default envConfigInitializer;
